import { query, request } from '../api'
import type { components } from '../api.types'

export type Activity = components['schemas']['Activity']
export type ActivityLibrary = components['schemas']['ActivityLibrary']
export type ExperimentPlan = components['schemas']['ExperimentPlan']
export type ExperimentPlanCreate = components['schemas']['ExperimentPlanCreate']
export type ExperimentRun = components['schemas']['ExperimentRun']
export type ExperimentRunPage = components['schemas']['ExperimentRunPage']
export type ExperimentCaptureEligibility = components['schemas']['ExperimentCaptureEligibility']
export type ExperimentCaptureAttempt = components['schemas']['ExperimentCaptureAttempt']
export type CustomActivityBounds = components['schemas']['CustomActivityBounds']
export type ExperimentEvent = components['schemas']['ExperimentEvent']
export type ExperimentReport = components['schemas']['ExperimentReport']
export type ExperimentResult = components['schemas']['ExperimentResult']
export type PlaybookPage = components['schemas']['PlaybookPage']
export type CaptureRole = NonNullable<components['schemas']['UploadRecordIngestRequest']['capture_role']>

const id = encodeURIComponent

/** Contract-only deployments answer the typed experiment.not_implemented Problem. */
export const listActivities = (signal?: AbortSignal) =>
  request<ActivityLibrary>('/api/v1/experiments/activities', { signal })

export const getPlan = (planId: string, signal?: AbortSignal) =>
  request<ExperimentPlan>(`/api/v1/experiments/${id(planId)}`, { signal })

export const createPlan = (body: ExperimentPlanCreate, key: string, signal?: AbortSignal) =>
  request<ExperimentPlan>('/api/v1/experiments', { method: 'POST', body, idempotencyKey: key, signal })

export const createRun = (planId: string, key: string, signal?: AbortSignal) =>
  request<ExperimentRun>(`/api/v1/experiments/${id(planId)}/runs`, {
    method: 'POST', body: {}, idempotencyKey: key, signal,
  })

export const getRun = (runId: string, signal?: AbortSignal) =>
  request<ExperimentRun>(`/api/v1/experiment-runs/${id(runId)}`, { signal })

export const postEvent = (runId: string, body: ExperimentEvent, key: string, signal?: AbortSignal) =>
  request<ExperimentRun>(`/api/v1/experiment-runs/${id(runId)}/events`, {
    method: 'POST', body, idempotencyKey: key, signal,
  })

export const postReport = (runId: string, body: ExperimentReport, key: string, signal?: AbortSignal) =>
  request<ExperimentRun>(`/api/v1/experiment-runs/${id(runId)}/report`, {
    method: 'POST', body, idempotencyKey: key, signal,
  })

export const skipReport = (runId: string, revision: number, key: string, signal?: AbortSignal) =>
  request<ExperimentRun>(`/api/v1/experiment-runs/${id(runId)}/report/skip`, {
    method: 'POST', body: { revision }, idempotencyKey: key, signal,
  })

export const getResult = (runId: string, options: { revision?: number; signal?: AbortSignal } = {}) =>
  request<ExperimentResult>(`/api/v1/experiment-runs/${id(runId)}/result${query({ revision: options.revision })}`, { signal: options.signal })

export const getPlaybook = (userId: string, options: { cursor?: string; limit?: number; signal?: AbortSignal } = {}) =>
  request<PlaybookPage>(`/api/v1/users/${id(userId)}/playbook${query({ cursor: options.cursor, limit: options.limit })}`, { signal: options.signal })

/** Counts use the complete owner-scoped list, never only its first page. */
export async function listRuns(userId: string, options: { activity?: string; signal?: AbortSignal } = {}): Promise<ExperimentRun[]> {
  const rows = new Map<string, ExperimentRun>()
  const cursors = new Set<string>()
  let cursor: string | null = null
  do {
    options.signal?.throwIfAborted()
    const page: ExperimentRunPage = await request<ExperimentRunPage>(`/api/v1/users/${id(userId)}/experiment-runs${query({
      activity: options.activity, limit: 100, cursor,
    })}`, { signal: options.signal })
    for (const row of page.items) rows.set(row.id, row)
    cursor = page.next_cursor ?? null
    if (cursor !== null) {
      if (cursors.has(cursor)) throw new Error('The experiment list repeated a page cursor.')
      cursors.add(cursor)
    }
  } while (cursor !== null)
  return [...rows.values()]
}
